import {
  alpha,
  Box,
  ButtonBase,
  Chip,
  Collapse,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Iconify from "../iconify/Iconify";
import { getDayOverview } from "../../pages/trainer/trainerPageData";
import {
  DAY_ORDER,
  DAY_SHORT_LABELS,
  formatDateLabel,
  SLOT_STATUS_META,
} from "../../pages/trainer/trainerUtils";
import TrainerSlotCard from "./TrainerSlotCard";

export default function TrainerWeekView({
  isMobile,
  weekDateValues,
  slotsByDate,
  todayValue,
  selectedDayIndex,
  mobileFocusCue,
  mobileWeekRevealRefs,
  onSelectWeekDay,
  onOpenSlot,
}) {
  const theme = useTheme();

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {weekDateValues.map((dateValue, index) => {
          const daySlots = slotsByDate.get(dateValue) || [];
          const dayOverview = getDayOverview(daySlots);
          const isExpanded = index === selectedDayIndex;
          const isToday = dateValue === todayValue;

          return (
            <Paper
              key={dateValue}
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                borderColor: isExpanded
                  ? alpha(theme.palette.primary.main, 0.35)
                  : isToday
                  ? alpha(theme.palette.primary.main, 0.22)
                  : alpha(theme.palette.divider, 0.7),
                borderWidth: isExpanded || isToday ? 2 : 1,
                boxShadow: isExpanded
                  ? mobileFocusCue === `week-${dateValue}`
                    ? `0 18px 36px ${alpha(theme.palette.primary.main, 0.16)}`
                    : `0 12px 28px ${alpha(theme.palette.primary.main, 0.08)}`
                  : "none",
                transition: "border-color 0.16s ease, box-shadow 0.16s ease",
              }}
            >
              <ButtonBase
                onClick={() => onSelectWeekDay(index, dateValue)}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  px: 1.5,
                  py: 1.35,
                  backgroundColor: isExpanded
                    ? alpha(theme.palette.primary.main, 0.04)
                    : "transparent",
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            color:
                              isExpanded || isToday
                                ? "primary.main"
                                : "text.primary",
                          }}
                        >
                          {formatDateLabel(dateValue, { weekday: "long" })}
                        </Typography>
                        {isToday && (
                          <Chip
                            size="small"
                            label="Heute"
                            sx={{
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12,
                              ),
                              color: "primary.main",
                            }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateLabel(dateValue, {
                          day: "2-digit",
                          month: "long",
                        })}
                      </Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={0.6}>
                      {daySlots.length > 0 && (
                        <Chip
                          size="small"
                          label={daySlots.length}
                          sx={{
                            minWidth: 28,
                            height: 24,
                            fontWeight: 800,
                            backgroundColor: alpha(theme.palette.text.primary, 0.06),
                            color: "text.primary",
                          }}
                        />
                      )}
                      <Iconify
                        icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                        width={20}
                        sx={{
                          color: isExpanded ? "primary.main" : "text.secondary",
                        }}
                      />
                    </Stack>
                  </Stack>

                  <Typography
                    variant="body2"
                    color={daySlots.length > 0 ? "text.primary" : "text.secondary"}
                    sx={{
                      fontWeight: daySlots.length > 0 ? 600 : 500,
                      lineHeight: 1.35,
                    }}
                  >
                    {dayOverview.summaryLabel}
                  </Typography>

                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                    {dayOverview.openCount > 0 && (
                      <StatusChip
                        label={`${dayOverview.openCount} offen`}
                        meta={SLOT_STATUS_META.open}
                      />
                    )}
                    {dayOverview.mineCount > 0 && (
                      <StatusChip
                        label={`${dayOverview.mineCount} meine`}
                        meta={SLOT_STATUS_META.mine}
                      />
                    )}
                    {dayOverview.occupiedCount > 0 && (
                      <StatusChip
                        label={`${dayOverview.occupiedCount} besetzt`}
                        meta={SLOT_STATUS_META.occupied}
                      />
                    )}
                    {dayOverview.firstSlot && (
                      <Chip
                        size="small"
                        label={`${dayOverview.firstSlot.session.start} ${
                          dayOverview.firstSlot.session.title || "Training"
                        }`}
                        sx={{
                          maxWidth: "100%",
                          height: 22,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          backgroundColor: alpha(theme.palette.text.primary, 0.05),
                          color: "text.secondary",
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
              </ButtonBase>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Divider />
                <Box
                  ref={(node) => {
                    if (node) {
                      mobileWeekRevealRefs.current[dateValue] = node;
                    } else {
                      delete mobileWeekRevealRefs.current[dateValue];
                    }
                  }}
                  sx={{
                    p: 1.5,
                    scrollMarginTop: 88,
                    background:
                      mobileFocusCue === `week-${dateValue}`
                        ? `linear-gradient(180deg, ${alpha(
                            theme.palette.primary.main,
                            0.06,
                          )}, transparent 85%)`
                        : "transparent",
                    transition: "background 0.32s ease",
                  }}
                >
                  {daySlots.length === 0 ? (
                    <EmptyDayCard />
                  ) : (
                    <Stack spacing={1}>
                      {daySlots.map((slot) => (
                        <TrainerSlotCard
                          key={`${slot.siteId}-${slot.date}-${slot.sessionKey}`}
                          slot={slot}
                          onClick={onOpenSlot}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: 1,
      }}
    >
      {weekDateValues.map((dateValue, index) => {
        const daySlots = slotsByDate.get(dateValue) || [];
        const isToday = dateValue === todayValue;

        return (
          <Paper
            key={dateValue}
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 2.5,
              minHeight: 140,
              borderColor: isToday
                ? alpha(theme.palette.primary.main, 0.5)
                : "divider",
              borderWidth: isToday ? 2 : 1,
            }}
          >
            <Stack spacing={0.75}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: isToday ? "primary.main" : "text.secondary",
                  }}
                >
                  {DAY_SHORT_LABELS[DAY_ORDER[index]] || ""}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: isToday ? 800 : 600,
                    color: isToday ? "primary.main" : "text.primary",
                  }}
                >
                  {formatDateLabel(dateValue, { day: "numeric" })}
                </Typography>
              </Stack>

              {daySlots.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ textAlign: "center", pt: 2 }}
                >
                  —
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {daySlots.map((slot) => (
                    <TrainerSlotCard
                      key={`${slot.siteId}-${slot.date}-${slot.sessionKey}`}
                      slot={slot}
                      compact
                      onClick={onOpenSlot}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Box>
  );
}

function StatusChip({ label, meta }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        fontSize: "0.68rem",
        fontWeight: 700,
        backgroundColor: meta.bg,
        color: meta.text,
      }}
    />
  );
}

function EmptyDayCard() {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2.5,
        textAlign: "center",
        borderStyle: "dashed",
      }}
    >
      <Iconify
        icon="mdi:calendar-blank-outline"
        width={28}
        sx={{ color: "text.disabled", mb: 0.5 }}
      />
      <Typography variant="body2" color="text.secondary">
        Kein Training an diesem Tag
      </Typography>
    </Paper>
  );
}
